import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Share2, Copy, Check } from 'lucide-react';
import { snapshotsApi } from '@/lib/snapshots';
import { useToast } from '@/components/ui';

interface Props {
  workspaceId: string;
  /** What kind of view this snapshot freezes — e.g. `'pipeline'`, `'signal_feed'`. */
  kind: string;
  /** A serializable freeze of the current view. Send whatever the caller wants to share. */
  payload: Record<string, unknown>;
  /** Human-readable label, e.g. "Pipeline · 2026-05-23". */
  label?: string;
  /** Days until the share URL expires. Default 30. */
  ttlDays?: number;
  className?: string;
}

/**
 * Drop-in "Share this view" button for any dashboard surface.
 * Creates a workspace snapshot, copies the public share URL to the
 * clipboard, shows a toast on success.
 *
 * The created URL is anyone-with-link readable for `ttlDays` days
 * (default 30). Backed by the existing /api/workspaces/:wsId/snapshots
 * endpoint + the public /share/:id reader page.
 */
export function ShareSnapshotButton({
  workspaceId,
  kind,
  payload,
  label,
  ttlDays = 30,
  className,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      snapshotsApi.create(workspaceId, {
        kind,
        label: label || `${kind} · ${new Date().toISOString().slice(0, 10)}`,
        payload,
        ttlDays,
      }),
    onSuccess: async (snap) => {
      const url = snapshotsApi.publicUrl(snap.id);
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success(t('snapshot.share_copied', { ttl: ttlDays }));
        setTimeout(() => setCopied(false), 2500);
      } catch {
        // Clipboard might be denied — still show the URL.
        toast.success(t('snapshot.share_created'));
      }
    },
    onError: () => toast.error(t('snapshot.share_failed')),
  });

  return (
    <button
      type="button"
      onClick={() => create.mutate()}
      disabled={create.isPending}
      className={
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-ink-300 text-sm font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-60 transition ' +
        (className || '')
      }
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5 text-emerald-600" />
          {t('snapshot.share_copied_short')}
        </>
      ) : create.isPending ? (
        <>
          <span className="h-3.5 w-3.5 rounded-full border-2 border-ink-400 border-t-ink-700 animate-spin" />
          {t('snapshot.sharing')}
        </>
      ) : (
        <>
          <Share2 className="h-3.5 w-3.5" />
          {t('snapshot.share_button')}
        </>
      )}
    </button>
  );
}
